import { AssignmentReturn } from "@mui/icons-material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { RequisitionEquipment } from "../../../types/model/warehouse/requisition";
import { formatTimestamp } from "../../../utils/date";

interface EquipmentReturnListProps {
  equipment: RequisitionEquipment[];
  canEdit: boolean;
  selectedIds: number[];
  isReturning: boolean;
  onToggle: (equipmentId: number) => void;
  onToggleAll: () => void;
  onReturn: (equipmentIds: number[]) => void;
}

export default function EquipmentReturnList({
  equipment,
  canEdit,
  selectedIds,
  isReturning,
  onToggle,
  onToggleAll,
  onReturn,
}: Readonly<EquipmentReturnListProps>) {
  const { t, i18n } = useTranslation();

  const pendingIds = equipment.filter((e) => !e.returnedOn).map((e) => e.equipmentId);
  const returnedCount = equipment.length - pendingIds.length;
  const allSelected = pendingIds.length > 0 && selectedIds.length === pendingIds.length;
  const someSelected = selectedIds.length > 0 && !allSelected;
  const canReturn = canEdit && pendingIds.length > 0;

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1, bgcolor: "action.hover" }}>
        {canReturn && (
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={someSelected}
            onChange={onToggleAll}
            disabled={isReturning}
            sx={{ ml: -1 }}
          />
        )}
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {t("warehouse.requisitions.equipment")}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          color={returnedCount === equipment.length ? "success" : "default"}
          icon={<CheckCircleOutlineIcon />}
          label={`${returnedCount}/${equipment.length}`}
        />
        {canReturn &&
          (selectedIds.length > 0 ? (
            <Button
              size="small"
              variant="contained"
              startIcon={isReturning ? <CircularProgress size={14} color="inherit" /> : <AssignmentReturn />}
              onClick={() => onReturn(selectedIds)}
              disabled={isReturning}
            >
              {t("warehouse.requisitions.return-selected")} ({selectedIds.length})
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={isReturning ? <CircularProgress size={14} color="inherit" /> : <AssignmentReturn />}
              onClick={() => onReturn(pendingIds)}
              disabled={isReturning}
            >
              {t("warehouse.requisitions.return-all")}
            </Button>
          ))}
      </Box>

      {equipment.map((item) => {
        const returned = !!item.returnedOn;
        const selected = selectedIds.includes(item.equipmentId);
        const selectable = canEdit && !returned && !isReturning;

        return (
          <React.Fragment key={item.equipmentId}>
            <Divider />
            <Box
              onClick={() => selectable && onToggle(item.equipmentId)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 0.75,
                cursor: selectable ? "pointer" : "default",
                opacity: returned ? 0.65 : 1,
                bgcolor: selected ? (theme) => alpha(theme.palette.primary.main, 0.08) : "transparent",
                "&:hover": selectable ? { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05) } : undefined,
              }}
            >
              {returned ? (
                <CheckCircleOutlineIcon color="success" fontSize="small" sx={{ mx: 1.25, ml: 0.25 }} />
              ) : (
                canEdit && (
                  <Checkbox
                    size="small"
                    checked={selected}
                    onChange={() => onToggle(item.equipmentId)}
                    onClick={(e) => e.stopPropagation()}
                    disabled={isReturning}
                    sx={{ ml: -1 }}
                  />
                )
              )}

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight="medium" noWrap>
                  {item.equipmentType}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {[item.equipmentModel, item.serialNumber].filter(Boolean).join(" · ")}
                </Typography>
              </Box>

              {item.returnedOn ? (
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                  {item.returnedAutomatically
                    ? t("warehouse.requisitions.returned-automatically-on")
                    : t("warehouse.requisitions.returned-on")}
                  : {formatTimestamp(item.returnedOn, i18n.language)}
                </Typography>
              ) : (
                canEdit && (
                  <Tooltip title={t("warehouse.requisitions.return")}>
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          onReturn([item.equipmentId]);
                        }}
                        disabled={isReturning}
                      >
                        <AssignmentReturn fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )
              )}
            </Box>
          </React.Fragment>
        );
      })}
    </Paper>
  );
}